<template>
    <div class="table-sticky-header">
        <b-button-toolbar
            v-if="isSelectable && hasItems"
            aria-label="Toolbar with button groups and dropdown menu"
        >
            <!-- Toolbar buttons for exporting data -->
            <export-toolbar
                class="export-toolbar"
                :exportData="exportData"
            />
            <!-- Toolbar button for tableResultRelations -->
            <relations-toolbar-button
                v-if="hasTableResultRelations"
                :exportData="exportData"
                :relations="tableResultRelations"
                :isTableResultTab="true"
            />
            <b-button size="sm ml-auto" @click="$_onShowFilter()">
                <img
                    v-if="showFilter"
                    src="~@/assets/icons/ng_fluent_filteroff_20_regular.svg"
                    :alt="$t('labelFiltersTurnOff')"
                    :title="$t('labelFiltersTurnOff')"
                />
                <img
                    v-else
                    src="~@/assets/icons/ng_fluent_filteron_20_regular.svg"
                    :alt="$t('labelFilters')"
                    :title="$t('labelFilters')"
                />
            </b-button>
        </b-button-toolbar>
        <!-- When loading is finished show message on error, no result or too many results -->
        <div v-if="!isStatusLoading && message" class="p-2">
            {{ message }}
        </div>
        <!-- Show table when ready and results are available -->
        <div
            v-if="isStatusReady && dataObject.count > 0"
            class="b-table-content-wrapper"
        >
            <div class="b-table-content">
                <div class="b-table-overflow">
                    <div class="b-table-sticky-header">
                        <b-table
                            ref="table"
                            :items="filteredItems"
                            :fields="fields"
                            class="table-styled"
                            small
                            sort-icon-left
                            :sortBy="sortByDefault"
                            :tbody-tr-class="$_rowClass"
                            @row-clicked="$_onRowClicked"
                            @sort-changed="$_onSortChanged"
                            :thead-class="showFilter ? 'has-filter' : ''"
                        >
                            <!-- Custom header cell with filter input field -->
                            <template #head()="data">
                                {{ data.field.label }}
                                <div>
                                    <input
                                        v-if="
                                            showFilter && data.field.filterable
                                        "
                                        v-model="filters[data.field.key]"
                                        @input="$_onInputFilter"
                                        type="search"
                                        class="input-filter"
                                    />
                                </div>
                            </template>
                            <!-- Default data cell scoped slot -->
                            <template #cell()="data">
                                <template
                                    v-if="
                                        data.field.hyperlink &&
                                        !data.field.hyperlinkImage
                                    "
                                >
                                    <a
                                        :href="$_getUrl(data)"
                                        :target="
                                            data.field.openExtern
                                                ? '_blank'
                                                : '_self'
                                        "
                                    >
                                        <template
                                            v-if="data.field.hyperlinkText"
                                        >
                                            {{ $_getHyperlinkText(data) }}
                                        </template>
                                        <template v-else-if="data.value">
                                            {{ data.value }}
                                        </template>
                                    </a>
                                </template>
                                <template
                                    v-else-if="
                                        data.field.hyperlink &&
                                        data.field.hyperlinkImage
                                    "
                                >
                                    <a
                                        :href="$_getUrl(data)"
                                        :target="
                                            data.field.openExtern
                                                ? '_blank'
                                                : '_self'
                                        "
                                    >
                                        <img
                                            class="detailimage"
                                            :src="$_getUrl(data)"
                                            alt="hyperlink"
                                        />
                                    </a>
                                </template>
                                <template v-else>
                                    {{ data.value }}
                                </template>
                            </template>
                            <!-- A custom formatted header cell for field 'selected' -->
                            <template #head(selected)>
                                <x-checkbox
                                    :id="`exportAll_${tabId}`"
                                    :name="`exportAll_${tabId}`"
                                    :value="hasTableSelection"
                                    @change="$_onSelectAllRows"
                                    :indeterminate="
                                        hasInderminateTableSelection
                                    "
                                >
                                </x-checkbox>
                            </template>
                            <!-- Custom field for 'selected' -->
                            <template v-slot:cell(selected)="row">
                                <x-checkbox
                                    :id="`${tabId}-${row.item.nedGlobeRowId}selected`"
                                    :name="`${tabId}-${row.item.nedGlobeRowId}selected`"
                                    :value="row.item.selected"
                                    @change="$_onSelectRow(row.item, $event)"
                                >
                                </x-checkbox>
                            </template>
                            <!-- Custom field for 'relation' (Stuf, Kvk, Brk) -->
                            <template v-slot:cell(relation)="row">
                                <b-link
                                    v-if="row.item.relation"
                                    :title="row.item.relation.title"
                                    @click.stop="$_openRelation(row.item)"
                                >
                                    <span
                                        v-if="row.item.relation.type === 'link'"
                                        >{{ row.item.relation.name }}</span
                                    >
                                    <div v-else class="report-icon"></div>
                                </b-link>
                            </template>
                            <!-- Custom field for 'tableResultRelation' -->
                            <template v-slot:cell(tableResultRelation)="row">
                                <!-- Dropdown button for tableResultRelations -->
                                <relations-toolbar-dropdown
                                    :relations="tableResultRelations"
                                    @openRelation="
                                        $_openTableResultRelation(
                                            [row.item],
                                            $event
                                        )
                                    "
                                />
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapActions, mapMutations } from 'vuex';
    import {
        TAB_STATUS,
        NEDGLOBE_SERVICES,
        RELATIONSHIP_TYPES
    } from '../../../../constants';
    import ExportToolbar from '../../information/toolbars/ExportObjectsToolbar.vue';
    import RelationsToolbarButton from '../../information/toolbars/RelationsToolbarButton.vue';
    import RelationsToolbarDropDown from '../../information/toolbars/RelationsToolbarDropDown.vue';
    import { getReplacedAttributeValue } from '../../../../services/selections';
    import { isSmallDevice } from '../../../../services/utilities/devices';

    export default {
        components: {
            'export-toolbar': ExportToolbar,
            'relations-toolbar-button': RelationsToolbarButton,
            'relations-toolbar-dropdown': RelationsToolbarDropDown
        },
        props: {
            dataObject: { type: Object, required: false },
            selectedObject: { type: Object, required: false }
        },
        data() {
            return {
                exportData: [],
                origFields: [],
                fields: [],
                items: [],
                activeRowId: 0,
                filters: [],
                showFilter: false
            };
        },
        created() {
            if (this.dataObject.count > 0) {
                this.$_showTab();
            }
        },
        computed: {
            ...mapGetters([
                'getTableResultRelations',
                'getLayerByOriginalId',
                'getTableSearchResultById',
                'getShowTableViewSmallDevices'
            ]),
            message() {
                let message = '';
                if (this.dataObject.message) {
                    message = this.dataObject.message;
                } else if (this.dataObject.count === 0) {
                    // Changed from items.length to dataObject.count
                    // For some reason items.length gives incorrect results when more than 1 tab open.
                    message = this.$t('noResultsFound');
                } else if (this.isMaxResultsExceeded) {
                    message = this.$t('stuf.maxResultsExceeded');
                }
                return message;
            },
            hasItems() {
                return this.dataObject?.count > 0;
            },
            isMaxResultsExceeded() {
                return this.dataObject?.maxResultsExceeded;
            },
            isSelectable() {
                return this.dataObject?.data?.selectable;
            },
            sortByDefault() {
                return this.dataObject?.data?.sortBy;
            },
            tabId() {
                return this.dataObject?.id;
            },
            hasTableSelection() {
                return !!this.items.find(({ selected }) => selected === true);
            },
            hasInderminateTableSelection() {
                return (
                    this.hasTableSelection &&
                    this.items.some(({ selected }) => selected === false)
                );
            },
            isStatusReady() {
                return this.dataObject?.status === TAB_STATUS.READY;
            },
            isStatusLoading() {
                return this.dataObject?.status === TAB_STATUS.LOADING;
            },
            isStatusError() {
                return this.dataObject?.status === TAB_STATUS.ERROR;
            },
            hasTableResultRelations() {
                return this.tableResultRelations.length > 0;
            },
            tableResultRelations() {
                return this.dataObject?.data?.tableSearchResult
                    ? this.getTableResultRelations(
                          this.dataObject.data.tableSearchResult
                      )
                    : [];
            },
            filteredItems() {
                const filtered = this.items.filter((item) => {
                    return Object.keys(this.filters).every((key) =>
                        String(item[key])
                            .toLowerCase()
                            .includes(this.filters[key].toLowerCase())
                    );
                });
                return filtered.length > 0 ? filtered : [];
            }
        },
        methods: {
            ...mapActions([
                'findReversedTableRelation',
                'findTable2TableRelation',
                'clearTabsRight',
                'setTabToFocus'
            ]),
            ...mapMutations(['UPDATE_OBJECT_DATA_ITEMS']),
            $_getUrl(item) {
                if (item.field.hyperlinkExpression) {
                    return item.value
                        ? getReplacedAttributeValue(
                              item.field.hyperlinkExpression,
                              item.value
                          )
                        : '';
                }
                return item.value;
            },
            $_getHyperlinkText(item) {
                return item.field.hyperlinkText && item.value
                    ? getReplacedAttributeValue(
                          item.field.hyperlinkText,
                          item.value
                      )
                    : '';
            },
            $_rowClass(item, type) {
                if (type === 'row-top') {
                    return this.showFilter ? '' : 'd-none';
                }
                if (!item || type !== 'row') {
                    return '';
                }
                if (item.nedGlobeRowId === this.activeRowId) {
                    return 'active';
                }
                return '';
            },
            $_addRelation() {
                this.items = this.items.map((item) => {
                    const showRelation = item.showRelation ?? true;
                    const relation = showRelation
                        ? this.dataObject.data.relation
                        : null;
                    return {
                        ...item,
                        relation
                    };
                });
                if (this.hasItems) {
                    this.fields.push({
                        key: 'relation',
                        label: '',
                        tdClass: 'sticky-col'
                    });
                }
            },
            $_addTableResultRelation() {
                if (this.hasItems) {
                    this.fields.push({
                        key: 'tableResultRelation',
                        label: '',
                        tdClass: 'sticky-col'
                    });
                }
            },
            async $_showTab() {
                if (this.dataObject?.data?.fields) {
                    this.origFields = JSON.parse(
                        JSON.stringify(this.dataObject.data.fields)
                    );
                    // Make headers uppercase
                    this.fields = this.origFields
                        .filter((field) => field.displayInTable ?? true)
                        .map((field) => {
                            return {
                                ...field,
                                label: field.label.toUpperCase(),
                                filterable: true
                            };
                        });
                    if (this.fields.length > 0) {
                        if (this.dataObject.data.selectable) {
                            this.fields.unshift({
                                key: 'selected',
                                label: '',
                                tdClass: 'checkboxcell',
                                thClass: 'checkboxcell'
                            });

                            this.items = this.dataObject.data.items.map((item) => ({
                                ...item,
                                selected: false
                            }));
                        }
                        if (this.dataObject.data.relation) {
                            this.$_addRelation();
                        }
                        if (this.hasTableResultRelations) {
                            this.$_addTableResultRelation();
                        }
                    }
                }
            },
            $_onSelectRow(item, val) {
                item.selected = val;

                const notAllowedColumns = [
                    'nedGlobeRowId',
                    'showRelation',
                    'selected',
                    'relation',
                    'tableResultRelation'
                ];
                this.exportData = this.$refs.table.sortedItems
                    .filter(({ selected }) => selected === true)
                    .map((_item) => {
                        return Object.keys(_item)
                            .filter((key) => !notAllowedColumns.includes(key))
                            .reduce((obj, key) => {
                                const field = this.origFields.find(
                                    ({ columnName }) => columnName === key
                                );
                                if (field?.exportable ?? true) {
                                    obj[key] = _item[key];
                                }
                                return obj;
                            }, {});
                    });
            },
            $_onSelectAllRows(val) {
                this.items.forEach((item) => {
                    this.$_onSelectRow(item, val);
                });
            },
            $_openRelation(data) {
                // We delete all data to the right of this tab.
                // We give tabToFocus a value so tabs.vue knows to set focus to the last tab.
                this.clearTabsRight({ title: this.dataObject.title });
                this.setTabToFocus({ title: this.dataObject.title });
                this.$store.dispatch(data.relation.action, { data });
            },
            $_onSortChanged() {
                this.UPDATE_OBJECT_DATA_ITEMS({
                    title: this.dataObject.title,
                    items: this.$refs.table.sortedItems
                });
            },
            $_onInputFilter() {
                // Reset all checkboxes when filter field is filled
                this.items.forEach((item) => {
                    item.selected = false;
                });
            },
            $_onRowClicked(item, index) {
                if (this.dataObject.data.rowClick) {
                    this.$store.dispatch(this.dataObject.data.rowClick.action, {
                        item
                    });
                }
                this.$emit('openObject', {
                    groupIndex: this.dataObject.id,
                    itemIndex: index
                });
            },
            async $_openTableResultRelation(data, relation) {
                // We delete all data to the right of this tab.
                // We give tabToFocus a value so tabs.vue knows to set focus to the last tab.
                this.clearTabsRight({ title: this.dataObject.title });
                this.setTabToFocus({ title: this.dataObject.title });
                // source is a themelayer
                if (relation.type === RELATIONSHIP_TYPES.ThemeLayer2Table) {
                    this.findReversedTableRelation({
                        title: this.dataObject.title,
                        data,
                        relation: { ...relation }
                    });
                }
                // source is a table
                if (relation.type === RELATIONSHIP_TYPES.Table2Table) {
                    this.findTable2TableRelation({
                        data,
                        relation: { ...relation }
                    });
                }
            },
            $_autoOpenRelations() {
                // Opens relation tabs automatically if started from advanced search
                if (
                    this.hasTableResultRelations &&
                    this.dataObject.count === 1 &&
                    this.dataObject.data?.isAdvancedSearch
                ) {
                    this.tableResultRelations
                        .sort((a, b) => a.order - b.order)
                        .forEach((relation, index) => {
                            let openRelation = false;
                            if (
                                relation.type ===
                                RELATIONSHIP_TYPES.ThemeLayer2Table
                            ) {
                                const themeLayer = this.getLayerByOriginalId(
                                    relation.fromThemeLayerId
                                );
                                if (
                                    themeLayer?.data.source.name ===
                                        NEDGLOBE_SERVICES &&
                                    themeLayer?.data.autoOpenAllRelationsTabs
                                ) {
                                    openRelation = true;
                                }
                            } else {
                                const objectSearchResult =
                                    this.$store.state.objectSearchResults[
                                        this.dataObject.id
                                    ];
                                const tableSearchResult =
                                    this.getTableSearchResultById(
                                        relation.fromTableSearchResultId
                                    );
                                if (
                                    tableSearchResult?.autoOpenAllRelationsTabs &&
                                    !objectSearchResult.relationIsOpened
                                ) {
                                    openRelation = true;
                                    // to avoid that relation is opened again when a objectSearchResult tab is closed
                                    this.$store.commit(
                                        'UPDATE_OBJECT_DATA_AUTO_OPENED_RELATION',
                                        index + 1
                                    );
                                }
                            }
                            if (openRelation) {
                                this.$_openTableResultRelation(
                                    [this.items[0]],
                                    relation
                                );
                            }
                        });
                }
            },
            $_onShowFilter() {
                this.showFilter = !this.showFilter;
            }
        },
        watch: {
            dataObject(newValue, oldValue) {
                if (
                    newValue !== null &&
                    newValue !== undefined &&
                    JSON.stringify(newValue) !== JSON.stringify(oldValue)
                ) {
                    this.$_showTab();

                    // Open relations automatically only if tableview is present
                    // Tableview is not present on small devices,
                    // except in web, when 'Show tableview on small devices' is checked
                    if (!isSmallDevice() || this.getShowTableViewSmallDevices) {
                        this.$_autoOpenRelations();
                    }
                }
                // Move map to record location when loading of data is ready
                // and there is exactly 1 record.
                if (
                    newValue.status != oldValue.status &&
                    newValue.status === TAB_STATUS.READY &&
                    this.dataObject.count === 1
                ) {
                    this.$store.dispatch('updateObjectSearchPointer', {
                        index: this.dataObject.id
                    });
                }
            },
            selectedObject: {
                handler: function (newVal) {
                    if (newVal) {
                        this.activeRowId = this.selectedObject.nedGlobeRowId;
                    } else {
                        this.activeRowId = null;
                    }
                },
                immediate: true
            },
            showFilter: {
                handler: function () {
                    this.filters = [];
                },
                immediate: true
            }
        }
    };
</script>

<style lang="scss" scoped>
.btn {
    border: 1px solid transparent;
}

.table-sticky-header {
    display: flex;
    flex-direction: column;

    & .export-toolbar {
        display: flex;
        padding: 4px 0px 4px 10px;
    }
}
</style>
